import { useEffect, useRef } from "react";
import { Navigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { MessageCircle } from "lucide-react";
import { api, formatApiError } from "../api";
import { AppHeader, BrandedLoading } from "../components/app";
import { ChatMessageList, toWsChatMessages } from "../components/chat";
import { RequireModerator } from "./AdminPages";
import { enrichMemberMatchesWithReports } from "./moderationMatches";
import type { AdminMemberMatch } from "./moderationMatches";
import { formatModerationTimestamp, reportStatusLabel, reportTypeLabel } from "./moderationReports";
import type { AdminUserReport } from "./moderationReports";

export function ModerationChatReviewPage() {
  const { id: memberId = "", matchId = "" } = useParams();
  const chatListRef = useRef<HTMLDivElement>(null);

  const { data: memberData, isLoading: memberLoading, error: memberError } = useQuery({
    queryKey: ["admin-user", memberId],
    queryFn: () => api.getAdminUser(memberId),
    enabled: Boolean(memberId),
  });

  const { data: matchesData, isLoading: matchesLoading } = useQuery({
    queryKey: ["admin-user-matches", memberId],
    queryFn: () => api.getAdminUserMatches(memberId),
    enabled: Boolean(memberId),
  });

  const memberMatches = enrichMemberMatchesWithReports(
    (matchesData?.matches ?? memberData?.matches ?? []) as AdminMemberMatch[],
    (memberData?.reports ?? []) as AdminUserReport[],
  );
  const match = memberMatches.find((entry) => entry.id === matchId) ?? null;

  const { data: chatData, isLoading: chatLoading, error: chatError } = useQuery({
    queryKey: ["admin-match-chat", matchId, memberId],
    queryFn: () => api.getAdminMatchChat(matchId, memberId),
    enabled: Boolean(memberId && matchId && match?.canViewChat),
  });

  const memberName = memberData?.profile?.firstName ?? "Member";
  const latestReport = match?.reports?.[0];
  const messages = toWsChatMessages(chatData?.messages ?? []);
  const isLoading = memberLoading || matchesLoading || chatLoading;
  const error = memberError || chatError;
  const headerTitle = match
    ? `${memberName} and ${match.otherFirstName}`
    : memberName !== "Member"
      ? `${memberName} and …`
      : "Chat review";

  useEffect(() => {
    const chatList = chatListRef.current;
    if (!chatList || !messages.length) return;
    chatList.scrollTop = chatList.scrollHeight;
  }, [messages]);

  if (!memberId || !matchId) {
    return <Navigate to="/admin/users" replace />;
  }

  return (
    <RequireModerator>
      <AppHeader
        title={headerTitle}
        subtitle={match?.statusLabel}
        backTo={`/admin/users/${memberId}`}
        desktopHidden
        edgeToEdge
      />

      {isLoading && <BrandedLoading message="Loading chat" />}

      {error && (
        <div className="banner error moderation-error">
          {formatApiError(error)}
        </div>
      )}

      {!isLoading && !error && !match && (
        <section className="chat-empty moderation-chat-empty">
          <MessageCircle size={30} />
          <h2>Match not found</h2>
          <p>This match could not be found for this member.</p>
        </section>
      )}

      {!isLoading && !error && match && !match.canViewChat && (
        <section className="chat-empty moderation-chat-empty">
          <MessageCircle size={30} />
          <h2>Chat unavailable</h2>
          <p>Chat is not available for this match.</p>
        </section>
      )}

      {!isLoading && !error && match?.canViewChat && (
        <div className="moderation-chat-review-body">
          <ChatMessageList
            messages={messages}
            viewerUserId={memberId}
            partnerName={match.otherFirstName}
            readOnly
            listRef={chatListRef}
            bottomSlot={latestReport ? (
              <aside className="moderation-chat-report-banner" aria-label="Report details">
                <div className="moderation-chat-report-banner-head">
                  <span className={`moderation-report-status moderation-report-status--${latestReport.status}`}>
                    {reportStatusLabel(latestReport.status)}
                  </span>
                  <time dateTime={latestReport.createdAt}>
                    {formatModerationTimestamp(latestReport.createdAt)}
                  </time>
                </div>
                <p className="moderation-chat-report-banner-type">
                  {reportTypeLabel(latestReport.type)}
                </p>
                <p className="moderation-chat-report-banner-body">{latestReport.description}</p>
              </aside>
            ) : undefined}
            emptyState={(
              <section className="chat-empty moderation-chat-empty">
                <MessageCircle size={30} />
                <h2>No messages</h2>
                <p>This conversation does not have any messages yet.</p>
              </section>
            )}
          />
        </div>
      )}
    </RequireModerator>
  );
}
